// Types consommés par l'interface pour les routes du socle métier (lot L0-C). Ils dérivent des
// contrats de l'API (courriers.ts, dossiers.ts, documents.ts, workflow.ts, recherche.ts) : un
// champ affiché est toujours un champ réellement renvoyé.
import type { Courrier } from './courriers';
import type { Dossier } from './dossiers';
import type { DocumentReponse } from './documents';
import type { ResultatRecherche } from './recherche';

/** Réponse de liste paginée par curseur (paramètre `apres`, curseur `suivant`). */
export interface ListePaginee<T> {
  elements: T[];
  /** Curseur de la page suivante, nul en fin de liste. */
  suivant?: string | null;
  total?: number;
}

export const ETATS_COURRIER_ARRIVEE = ['enregistre', 'affecte', 'en_traitement', 'repondu', 'classe', 'archive'] as const;
export const ETATS_COURRIER_DEPART = ['brouillon', 'en_validation', 'valide', 'signe', 'envoye', 'archive'] as const;

export const LIBELLES_ETATS_COURRIER: Record<string, string> = {
  enregistre: 'Enregistré',
  affecte: 'Affecté',
  en_traitement: 'En traitement',
  repondu: 'Répondu',
  classe: 'Classé',
  archive: 'Archivé',
  brouillon: 'Brouillon',
  en_validation: 'En validation',
  valide: 'Validé',
  signe: 'Signé',
  envoye: 'Envoyé',
};

export const LIBELLES_CANAUX_COURRIER: Record<string, string> = {
  papier: 'Papier',
  courriel: 'Courriel',
  portail: 'Portail',
  main_propre: 'Remis en main propre',
  poste: 'Voie postale',
};

export const LIBELLES_CONFIDENTIALITE: Record<string, string> = {
  ordinaire: 'Diffusion ordinaire',
  restreint: 'Restreint',
  confidentiel: 'Confidentiel',
};

export type CourrierResume = Courrier;

export interface TransitionDisponible {
  code: string;
  libelle: string;
  vers: string;
  motif_obligatoire: boolean;
}

export interface CourrierDetail extends CourrierResume {
  transitions_possibles: TransitionDisponible[];
  reponses: { id: string; numero: string; etat: string }[];
}

/** Événement d'historique d'un courrier ou d'un dossier (workflow_evenement). */
export interface EvenementHistorique {
  id: string;
  type: string;
  transition_code: string | null;
  etat_avant: string | null;
  etat_apres: string;
  horodatage: string;
  motif: string | null;
  documents: string[];
  delegation_id: string | null;
  donnees: Record<string, unknown>;
  auteur: { compte_id: string | null; appartenance_id: string | null; nom: string | null } | null;
  service_id: string | null;
}

export const ETATS_DOSSIER = [
  'brouillon',
  'depose',
  'enregistre',
  'en_verification',
  'complement_demande',
  'en_instruction',
  'en_validation',
  'transmis',
  'en_attente_decision',
  'decide',
  'en_signature',
  'notifie',
  'cloture',
  'archive',
  'classe_sans_suite',
  'retire',
] as const;

export const LIBELLES_ETATS_DOSSIER: Record<string, string> = {
  brouillon: 'Brouillon',
  depose: 'Déposé',
  enregistre: 'Enregistré',
  en_verification: 'En vérification',
  complement_demande: 'Complément demandé',
  en_instruction: 'En instruction',
  en_validation: 'En validation',
  transmis: 'Transmis',
  en_attente_decision: 'En attente de décision',
  decide: 'Décidé',
  en_signature: 'En signature',
  notifie: 'Notifié',
  cloture: 'Clôturé',
  archive: 'Archivé',
  classe_sans_suite: 'Classé sans suite',
  retire: 'Retiré',
};

/** Les sept statuts usager du flux de progression (H3-03), par libellé renvoyé par l'API. */
export const STATUTS_USAGER = [
  'Enregistré',
  'En vérification',
  'Incomplet',
  'En instruction',
  'En attente de décision',
  'Approuvé ou refusé',
  'Terminé',
] as const;

export const LIBELLES_RESULTAT: Record<string, string> = {
  approuve: 'Approuvé',
  approuve_sous_conditions: 'Approuvé sous conditions',
  refuse: 'Refusé',
};

export type DossierResume = Dossier;

export interface PieceDossier {
  id: string;
  type_piece: string;
  document_id: string;
  titre: string;
  verdict: 'non_examinee' | 'conforme' | 'non_conforme';
  motif: string | null;
  examinee_le: string | null;
}

export const LIBELLES_VERDICT: Record<string, string> = {
  non_examinee: 'Non examinée',
  conforme: 'Conforme',
  non_conforme: 'Non conforme',
};

export interface DossierDetail extends DossierResume {
  pieces: PieceDossier[];
  pieces_attendues: { type: string; libelle: string; obligatoire: boolean }[];
  transitions_possibles: TransitionDisponible[];
}

export interface NoteInterne {
  id: string;
  contenu: string;
  auteur: { appartenance_id: string; nom: string | null };
  cree_le: string;
}

export type DocumentResume = DocumentReponse;

export type ResultatsRecherche = ResultatRecherche;

/** Fiche d'indicateur (GET /indicateurs/catalogue). */
export interface FicheIndicateur {
  code: string;
  libelle: string;
  definition?: string;
  formule?: string;
  source?: string;
  denominateur?: string | null;
  module?: string;
}

// Valeur et liste explicative d'un indicateur : types ValeurIndicateur et ListeIndicateur du contrat
// réel (indicateurs.ts, exportés par @cc/contrats), qui remplacent ceux proposés ici par le lot L0-D
// (couverture en texte, filtres en JSON, liste sans total).
